import { Location } from "@angular/common";
import { Inject, NgModule, Pipe, PipeTransform } from "@angular/core";

import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";

/**
 * @description
 *
 * Generates a link to an HTML element on the same page as native anchor doesn't work.
 *
 * Quick explanation : Due to how Angular Router works, a simple anchor to element isn't enough to provide a link to an element on the same page.
 * Every route is forwarded to root file `index.html`, therefore any anchor is based on root url, causing standard fragments to redirect to root url.
 * A way to bypass this behavior is to provide a complete path to element.
 * As an example, when scrolling to `#someId` on `https://domain.name/complete/path`, this is how the `href` attribute should look like `/complete/path#someId`.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Linking_to_an_element_on_the_same_page
 *
 * The `hSkipLink` pipe is most commonly used to ensure that skip links works as expected (no redirect, scroll to instead).
 *
 * @usageNotes
 *
 * Provide an anchor to an HTML element with a specific id.
 * ```
 * <a [href]="'#idValue' | hSkipLink">...</a>
 * ```
 *
 * Provide an anchor to the top.
 * ```
 * <a [href]="'#' | hSkipLink">...</a>
 * <a [href]="'#top' | hSkipLink">...</a>
 * ```
 *
 * Works as native anchor when no HTML element matches the id (does nothing on click, keyboard.enter or keyboard.space).
 */
@Pipe({
    name: "hSkipLink",
})
export class SkipLinkPipe implements PipeTransform {
    constructor(
        @Inject(LOCALE) private locale: Locale,
        private location: Location,
    ) {}

    public transform(anchor: string): string {
        return [
            this.locale.urlPrefix,
            // No hash so anchor can override existing fragments (intended)
            this.location.path(),
            anchor,
        ].join("");
    }
}

@NgModule({
    declarations: [SkipLinkPipe],
    exports: [SkipLinkPipe],
})
export class SkipLinkPipeModule {}
